import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Activity, CheckCircle, AlertTriangle, XCircle, Wifi, WifiOff } from 'lucide-react';

interface ProviderHealth {
  providerId: string;
  providerName: string;
  providerSlug: string;
  logoUrl: string | null;
  channelType: string;
  totalInboxes: number;
  activeInboxes: number;
  connectingInboxes: number;
  errorInboxes: number;
  disconnectedInboxes: number;
  inactiveInboxes: number;
  healthScore: number;
}

interface ProviderHealthCardProps {
  data: ProviderHealth[];
  isLoading?: boolean;
}

function getHealthColor(score: number): string {
  if (score >= 80) return 'text-green-500';
  if (score >= 50) return 'text-yellow-500';
  return 'text-red-500';
}

function getHealthBg(score: number): string {
  if (score >= 80) return 'bg-green-500';
  if (score >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
}

function getHealthLabel(score: number): string {
  if (score >= 80) return 'Saudável';
  if (score >= 50) return 'Atenção';
  return 'Crítico';
}

function getChannelBadgeStyle(channelType: string): string {
  switch (channelType) {
    case 'whatsapp': return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'messenger': return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    case 'instagram': return 'bg-pink-500/10 text-pink-600 border-pink-500/20';
    case 'telegram': return 'bg-sky-500/10 text-sky-600 border-sky-500/20';
    case 'webchat': return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
    default: return 'bg-muted text-muted-foreground';
  }
}

export function ProviderHealthCard({ data, isLoading }: ProviderHealthCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Saúde dos Providers
        </CardTitle>
        <CardDescription>Status das integrações por provider</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="flex-1">
                  <Skeleton className="h-4 w-32 mb-2" />
                  <Skeleton className="h-2 w-full" />
                </div>
                <Skeleton className="h-6 w-16" />
              </div>
            ))}
          </div>
        ) : data.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Nenhum provider com inboxes configuradas.
          </p>
        ) : (
          <div className="space-y-4">
            {data.map((provider) => (
              <div key={provider.providerId} className="space-y-2">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    {provider.logoUrl ? (
                      <img 
                        src={provider.logoUrl} 
                        alt={provider.providerName}
                        className="h-8 w-8 rounded-lg object-contain bg-muted p-1"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                        <Wifi className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{provider.providerName}</p>
                      <Badge variant="outline" className={`text-[10px] ${getChannelBadgeStyle(provider.channelType)}`}>
                        {provider.channelType}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-sm font-bold ${getHealthColor(provider.healthScore)}`}>
                      {provider.healthScore}%
                    </span>
                    <Badge variant="outline" className={`text-xs ${getHealthColor(provider.healthScore)}`}>
                      {getHealthLabel(provider.healthScore)}
                    </Badge>
                  </div>
                </div>
                
                <Progress 
                  value={provider.healthScore} 
                  className="h-2"
                />
                
                <div className="flex flex-wrap gap-3 text-xs">
                  <div className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    <span className="text-muted-foreground">{provider.activeInboxes} ativas</span>
                  </div>
                  {provider.connectingInboxes > 0 && (
                    <div className="flex items-center gap-1">
                      <Wifi className="h-3 w-3 text-blue-500" />
                      <span className="text-muted-foreground">{provider.connectingInboxes} conectando</span>
                    </div>
                  )}
                  {provider.errorInboxes > 0 && (
                    <div className="flex items-center gap-1">
                      <XCircle className="h-3 w-3 text-red-500" />
                      <span className="text-red-500 font-medium">{provider.errorInboxes} erro</span>
                    </div>
                  )}
                  {provider.disconnectedInboxes > 0 && (
                    <div className="flex items-center gap-1">
                      <WifiOff className="h-3 w-3 text-yellow-500" />
                      <span className="text-yellow-500 font-medium">{provider.disconnectedInboxes} desconectadas</span>
                    </div>
                  )}
                  {provider.inactiveInboxes > 0 && (
                    <div className="flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3 text-muted-foreground" />
                      <span className="text-muted-foreground">{provider.inactiveInboxes} inativas</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
